import {Assert, GetPercentFromXToY} from "js-vextensions";
import {AssertUnreachable, minuteInMS, secondInMS} from "web-vcore";
import {DetectBreathingPeaks, Peak} from "../../../Engine/LiveSession/Components/Breath/PeakDetection/PeakDetection_Prominence.js";
import {PoliceSiren_GetCyclePeaksInRange, PoliceSiren_GetSlopesOfSamplesMatchingGivenTimes} from "../../../Engine/LiveSession/Components/Breath/SirenSound.js";
import {SessionEvent, SessionEvent_SirenConfig} from "../../../Engine/LiveSession/SessionEvent.js";
import {DesyncCalcType, EngineConfig_Breath} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_Breath.js";
import {Session} from "../../../Server/DB/sessions/@Session.js";
import {SessionExternals} from "../../../Store/main/timeline/SessionDerivativeData.js";
import {BinarySearch} from "../../../Utils/General/General.js";
import {BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG, BREATH_INTERVAL_CALC_WINDOW_SIZE_SHORT, BREATH_LINE_TRAVEL_DISTANCE_CALC_WINDOW_SIZE, BREATH_PEAKS_CALC_MIN_DIST_BETWEEN_PEAKS_DEFAULT, BREATH_PEAKS_CALC_WINDOW_SIZE_DEFAULT, BREATH_VALUE_STD_DEV_CALC_WINDOW_SIZE, BREATH_VARIABILITY_ON_INTERVAL_CALC_WINDOW_SIZE, BREATH_EDGE_SHIFT_CALC_WINDOW_SIZE, BREATH_INTERVAL_PERCENTILE_X_CALC_WINDOW_SIZE, BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_X, BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_Y} from "../../Timeline/Sessions/SessionUI/SessionChartColors.js";
import {store} from "../../../Store/index.js";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

export const Belt_samplesPerSecond = 10;
export const Belt_sampleInterval = 1000 / Belt_samplesPerSecond;

export type BreathSample = {value: number, time: number};

export type LongInhale = {
	timeOfSignalStart: number; // timestamp of first breath-sample that was part of the inhale period
	timeOfSignalCompleted: number; // timestamp of last breath-sample that was part of the inhale period (frozen at first sample when detectable/detected)
};

export type EngineConfig_Breath_ForBreathProcessor = Pick<EngineConfig_Breath,
	"maxSampleGapInSequence_base"
	| "sync_desyncCalcType" | "sync_calcDesyncOverXSeconds_long" | "sync_calcDesyncOverXSeconds_short" | "sync_responseTime" | "sync_minBreathPeakProminence" | "sync_minBreathPeakHeight"
	| "longInhaleDetectionConfigs"
>;

/** Per-sample instrumentation data recorded inline within `UpdateLongInhales`, relative to the window's start sample (the trough). The start-sample itself is identified by the map-key; see `BreathProcessor.longInhaleDebugData`. */
export type LongInhaleDebugSample = {
	/** Duration (in ms) from window-start to this sample. */
	durationSoFar: number;
	/** Ascend-height (running) from window-start up to this sample. */
	ascentSoFar: number;
	/** Roughness (coefficient of variation) of the inhale-samples up to this sample. Computed freshly per sample (needs >= 2 samples); null until then. */
	roughness: number|n;
};

export class BreathProcessorOptions {
	constructor(data: BreathProcessorOptions) { Object.assign(this, data); }
	breathCompConfig!: EngineConfig_Breath_ForBreathProcessor;
	/** If set (both non-null), `UpdateLongInhales` records per-sample instrumentation data for samples whose time falls in [longInhaleDebug_startTime, longInhaleDebug_endTime], for the sliding-window whose trough matches the start sample. (used by the detail-panel "testing" tab) */
	longInhaleDebug_startTime?: number|n;
	longInhaleDebug_endTime?: number|n;
}
export class BreathProcessor {
	/*constructor(options: RequiredBy<Partial<BreathProcessorOptions>, "breathCompConfig">) {
		this.options = Object.assign(new BreathProcessorOptions(), options);
	}*/
	// one of these Init methods should be called immediately after construction
	Init_LiveSession(options: BreathProcessorOptions) {
		Object.assign(this, new BreathProcessor()); // first ensure all fields are set to their initial values
		this.options = options;

		return this; // return instance, for convenience chaining
	}
	Init_SavedSession(session: Session, ext: SessionExternals, options: BreathProcessorOptions) {
		Object.assign(this, new BreathProcessor()); // first ensure all fields are set to their initial values
		this.options = options;

		const newSamples_force = [] as {value: number, time: number}[];
		for (const chunk of ext.respirationDataChunks) {
			let currentSampleTime = chunk.startTime;
			for (const sample of chunk.samples_force) {
				const sampleWithTime = {value: sample, time: currentSampleTime};
				newSamples_force.push(sampleWithTime);
				currentSampleTime += Belt_sampleInterval;
			}
		}

		this.breathPeaks = [];
		this.breathPeaks_forceSamplesAtLastUpdate = 0;
		for (const sample of newSamples_force.OrderBy(a=>a.time)) {
			this.AddBeltForceSample(sample.value, sample.time);
		}

		// todo: probably change this+BreathComp someday, to use exactly the same processing pipeline
		// (currently, the two routes should produce the same results, practically speaking; but there's technically some room for divergence, eg. breath-peak detection done in-one-call vs chunk-by-chunk)

		// derivation level 1
		this.UpdateBreathPeaks();
		this.CalculateSirenStarts(session.events);
		// derivation level 2
		this.CalculateSirenSamples();
		this.CalculateDesyncAmountSamples();
		this.CalculateBreathIntervalSamples_Long();
		this.CalculateBreathIntervalSamples_Short();
		this.CalculateBreathIntervalPercentileXSamples();
		this.CalculateBreathIntervalVariabilitySamples();
		this.CalculateBreathEdgeShiftSamples();
		this.UpdateLongInhales(session.events, null);
	}

	options!: BreathProcessorOptions; // !: by Init_X
	get Config() { return this.options.breathCompConfig; }

	get SampleCount() { return this.samples_force.length; }
	samples_force = [] as BreathSample[];

	// derived data (only populated for saved-sessions atm -- and external owners of the processor that call processor.CalculateSirenSamples())
	// ----------

	// derivation level 1
	breathPeaks = [] as Peak[]; // calculated in CalculateBreathPeaks()
	breathPeaks_forceSamplesAtLastUpdate = 0;
	sirenStarts = [] as {time: number, sirenConfig: SessionEvent_SirenConfig}[]; // siren-start events, calculated in CalculateSirenStarts()

	// derivation level 2 (ie. requires the fields above to be populated first)
	samples_siren = [] as {value_basic: number, value_forDetailChart: number}[]; // siren samples, derived from the force-samples (eg. for visualization in the detail-chart)
	samples_desync = [] as {desyncAmount: number, desyncAmount_long: number, desyncAmount_short: number}[]; // desync-amount samples, derived from force-samples (eg. for visualization in the detail-chart)
	samples_breathInterval_long = [] as (number|null)[];
	samples_breathInterval_short = [] as (number|null)[];
	samples_breathIntervalPercentileX = [] as (number|null)[];
	samples_breathIntervalVariability = [] as (number|null)[];
	samples_breathEdgeShift = [] as (number|null)[];
	longInhales = [] as LongInhale[]; // calculated in UpdateLongInhales()
	/** Per-sample instrumentation data recorded inline within `UpdateLongInhales`, keyed by sample-index in `samples_force`. Only populated for samples within [options.longInhaleDebug_startTime, options.longInhaleDebug_endTime], and only for the sliding-window whose trough matches the debug-window's start sample (the user's chosen window-start). */
	longInhaleDebugData = new Map<number, LongInhaleDebugSample>();

	/**
	 * Returns the long-inhale debug-sample whose time is closest to (at-or-before) the given hovered time, along with the underlying breath-sample and its index in `samples_force`.
	 * Returns null if no instrumentation data is recorded, or the hovered time is before/outside the recorded window.
	 */
	GetLongInhaleDebugSampleAtTime(hoveredTime: number|n): {sampleIndex: number, sample: BreathSample, debug: LongInhaleDebugSample}|n {
		if (hoveredTime == null || this.samples_force.length == 0 || this.longInhaleDebugData.size == 0) return null;
		// find the nearest sample-index whose time is <= hoveredTime
		const idx = Math.max(0, BinarySearch(this.samples_force, s=>hoveredTime - s.time).GetIndexOfMatch_OrInsertPointPlusX(0) - 1);
		// only return if that index actually has debug-data recorded (ie. is within the analyzed window)
		if (!this.longInhaleDebugData.has(idx)) return null;
		return {sampleIndex: idx, sample: this.samples_force[idx], debug: this.longInhaleDebugData.get(idx)!};
	}

	/** Both window-start and window-end times are inclusive. Note that `this.breathPeaks` must be populated before calling this. */
	GetBreathPeaksInRange_FromPrecalcData(opt: {rangeStartTime: number, rangeEndTime: number|n, searchBackFromPeakIndex?: number|n}): Peak[] {
		if (opt.searchBackFromPeakIndex != null) {
			const result = [] as Peak[];
			for (let i = opt.searchBackFromPeakIndex; i >= 0; i--) {
				const peak = this.breathPeaks[i];
				if (opt.rangeEndTime != null && peak.sample.time > opt.rangeEndTime) continue; // skip if peak is after the end of the range
				if (peak.sample.time < opt.rangeStartTime) break;
				result.unshift(peak);
			}
			return result;
		}

		Assert(opt.rangeEndTime != null, "If searchBackFromPeakIndex mode is not used, then rangeEndTime must be provided. (code could be changed to only require rangeStartTime here, but this situation is probably a mistake)");
		const firstPeakInRange_index = BinarySearch(this.breathPeaks, peak=>opt.rangeStartTime - peak.sample.time).GetIndexOfMatch_OrInsertPointPlusX(0);
		const lastPeakInRange_index = BinarySearch(this.breathPeaks, peak=>opt.rangeEndTime! - peak.sample.time, {minI: firstPeakInRange_index}).GetIndexOfMatch_OrInsertPointPlusX(-1);
		return this.breathPeaks.slice(firstPeakInRange_index, lastPeakInRange_index + 1);
	}

	// ----------

	AddBeltForceSample(sampleValue: number, timeAssigned: number) {
		// if testing breath-data override is enabled, replace the sample-value with a fake one, matching the given breath-interval
		if (store.main.testing.overrideBreathData) {
			const firstSampleTime = this.samples_force.length > 0 ? this.samples_force[0].time : timeAssigned;
			const phase = (timeAssigned - firstSampleTime) / (store.main.testing.overrideBreathData_breathInterval * 1000);
			const trianglePhase = (phase % 1) * 4; // 0-4 over one cycle
			const artificalBreathValue_0to1 = trianglePhase < 2 ? trianglePhase / 2 : (4 - trianglePhase) / 2; // triangle wave 0-1 (0, .5, 1, .5, repeat)
			sampleValue = artificalBreathValue_0to1 * 5; // scale to 0-5 (closer to actual breath-value ranges)
		}

		this.samples_force.push({value: sampleValue, time: timeAssigned});
	}
	GetNowTime() { return this.samples_force.length > 0 ? this.samples_force.Last().time : Date.now(); }

	// derivation level 1
	// ==========

	/*CalculateBreathPeaks() {
		this.breathPeaks = this.GetBreathCyclePeaksInRange(0, Number.MAX_SAFE_INTEGER);
	}*/
	UpdateBreathPeaks_IfXSamplesSinceLastUpdate(minSamplesSinceLastUpdate: number) {
		// every 5 samples (ie. 0.5 seconds), check if there are new breath-peaks
		if (this.samples_force.length - this.breathPeaks_forceSamplesAtLastUpdate >= minSamplesSinceLastUpdate) {
			this.UpdateBreathPeaks();
		}
	}
	UpdateBreathPeaks() {
		const lastOldPeak = this.breathPeaks.LastOrX();
		const peaksInNewRegion = this.GetBreathPeaksInRange(
			// extend range back even further (by peak-detection window-size) so peak detection works well even at start of target range
			(this.samples_force[this.breathPeaks_forceSamplesAtLastUpdate]?.time ?? 0) - BREATH_PEAKS_CALC_WINDOW_SIZE_DEFAULT,
			this.samples_force.LastOrX()?.time ?? Date.now(),
		);
		for (const newPeak of peaksInNewRegion) {
			// if the new peak is older than the last old-peak, ignore it
			if (lastOldPeak && newPeak.sample.time <= lastOldPeak.sample.time) continue;
			// if the new peak is too close to the last old-peak, ignore it
			if (lastOldPeak && newPeak.sample.time.Distance(lastOldPeak.sample.time) < BREATH_PEAKS_CALC_MIN_DIST_BETWEEN_PEAKS_DEFAULT) continue;
			// if we reach here, the new breath-peak appears valid; add it to the list
			this.breathPeaks.push(newPeak);
		}
		this.breathPeaks_forceSamplesAtLastUpdate = this.samples_force.length;
	}

	CalculateSirenStarts(sessionEvents: SessionEvent[]) {
		this.sirenStarts = [] as {time: number, sirenConfig: SessionEvent_SirenConfig}[];
		if (sessionEvents.Any(a=>a.type == "Breath.SirenStart")) {
			for (const event of sessionEvents) {
				if (event.type == "Breath.SirenStart") {
					Assert(event.sirenConfig != null, "SirenStart event must have sirenConfig set.");
					this.sirenStarts.push({time: event.date, sirenConfig: event.sirenConfig});
				}
			}
			this.sirenStarts.sort((a, b)=>a.time - b.time); // ensure chronological order
		}
	}

	// derivation level 2
	// ==========

	/** Before calling, fields [samples_force, sirenStarts] must be populated. */
	CalculateSirenSamples() {
		this.samples_siren = this.samples_force.map(a=>({value_basic: 0, value_forDetailChart: 0}));

		if (this.sirenStarts.length > 0) {
			const forceSamples_shiftedToAccountForResponseTime = this.samples_force.map(a=>a.time - (this.Config.sync_responseTime * 1000));
			const yValForPeak = 0.3;

			type SirenPlayPeriod = {startTime: number, sirenConfig: SessionEvent_SirenConfig|n, forceSamples: number[]};
			const sirenPlayPeriods = [] as SirenPlayPeriod[];

			// start with a config-less siren period; this way, if the "Breath.SirenStart" event was lost or something, the earlier force-samples will at least get "0" values inserted for them (avoiding mis-alignment)
			let currentPlayPeriod: SirenPlayPeriod = {startTime: 0, sirenConfig: null, forceSamples: []};
			const finalizePlayPeriod = (period: SirenPlayPeriod, endTime: number|n)=>{
				period.forceSamples = forceSamples_shiftedToAccountForResponseTime.filter(a=>a >= period.startTime && (endTime == null || a < endTime));
				sirenPlayPeriods.push(period);
			};
			for (const sirenStart of this.sirenStarts) {
				if (currentPlayPeriod) finalizePlayPeriod(currentPlayPeriod, sirenStart.time);
				currentPlayPeriod = {startTime: sirenStart.time, sirenConfig: sirenStart.sirenConfig, forceSamples: []};
			}
			if (currentPlayPeriod) finalizePlayPeriod(currentPlayPeriod, null);

			let overallForceSampleIndex = 0;
			for (const period of sirenPlayPeriods) {
				const slopes = period.sirenConfig ? PoliceSiren_GetSlopesOfSamplesMatchingGivenTimes(period.forceSamples, period.startTime, period.sirenConfig.cycleTime) : period.forceSamples.map(a=>({value: 0})); // if no config, just use zeroes
				for (const slope of slopes) {
					this.samples_siren[overallForceSampleIndex] = {value_basic: slope.value, value_forDetailChart: 9.7 + (slope.value * yValForPeak)};
					overallForceSampleIndex++;
				}
			}
		}
	}

	// desync-amount calculation
	// ----------

	/** Before calling, fields [samples_force, sirenStarts] must be populated. */
	CalculateDesyncAmountSamples() {
		this.samples_desync = this.samples_force.map(a=>{
			const desyncAmount = this.GetDesyncAmount_CombinedFromTwoWindows(a.time, false);
			return {desyncAmount: desyncAmount.combined ?? 0, desyncAmount_long: desyncAmount.forLongWindow ?? 0, desyncAmount_short: desyncAmount.forShortWindow ?? 0};
		});
	}
	/** Before calling, fields [samples_force, sirenStarts] must be populated. */
	GetDesyncAmount_CombinedFromTwoWindows(endTime: number, forLiveSession: boolean) {
		// todo: probably switch this to use this.breathPeaks in both cases (now that we are calculating the breath-peaks for live sessions as well -- well, adjust BreathComp code to always calculate it, when making this change)
		const breathPeaksOverride = forLiveSession ? null : this.breathPeaks;

		const desyncAmount_longWindow = this.GetDesyncAmountForWindow(endTime - (this.Config.sync_calcDesyncOverXSeconds_long * 1000), endTime, breathPeaksOverride, "long", forLiveSession);
		const desyncAmount_shortWindow = this.GetDesyncAmountForWindow(endTime - (this.Config.sync_calcDesyncOverXSeconds_short * 1000), endTime, breathPeaksOverride, "short", forLiveSession);
		const desyncAmount_combined = (()=>{
			// if either window doesn't have enough samples to calculate its desync-amount, return null to signify that we can't calculate the final desync amount
			if (desyncAmount_longWindow == null || desyncAmount_shortWindow == null) {
				return null;
			}
			// Use the lower of the two desync amounts. Thus:
			// * The short-window lets the user quickly bring down the volume by correcting their breathing.
			// * The long-window keeps briefly-chaotic breathing from quickly causing the volume to spike up.
			return Math.min(desyncAmount_longWindow, desyncAmount_shortWindow);
		})();
		return {combined: desyncAmount_combined, forLongWindow: desyncAmount_longWindow, forShortWindow: desyncAmount_shortWindow};
	}
	/** Before calling, fields [samples_force, sirenStarts] must be populated. */
	GetDesyncAmountForWindow(startTime: number, endTime: number, recentPeaks_breath_override: Peak[]|null, type: "short" | "long", forLiveSession: boolean) {
		const {sync_desyncCalcType: calcType, sync_responseTime: responseTime} = this.Config;
		const windowSize = endTime - startTime;
		const currentSirenStart = this.sirenStarts.filter(a=>a.time <= startTime).OrderBy(a=>a.time).LastOrX();
		if (currentSirenStart == null) return 0; // no siren-config associated, so we can't calculate desync

		if (calcType == DesyncCalcType.peaks) {
			const recentPeaks_siren = PoliceSiren_GetCyclePeaksInRange(startTime, endTime, currentSirenStart.time, currentSirenStart.sirenConfig.cycleTime);
			if (recentPeaks_siren.length == 0) return 0; // no siren peaks in the last X seconds, so we can't calculate desync
			const recentPeaks_breath = recentPeaks_breath_override ?? this.GetBreathPeaksInRange(startTime - windowSize, endTime);
			if (recentPeaks_breath.length == 0) return 1; // no breath peaks in the last X seconds, so max desync

			const recentPeaks_desyncAmounts = [] as number[];
			for (const [i, sirenPeak] of recentPeaks_siren.entries()) {
				const closestBreathPeaksBeforeAndAfter = BinarySearch(recentPeaks_breath, peak=>sirenPeak.time - peak.sample.time).GetMatch_OrEntriesSurroundingInsertPoint();

				// expand the consider-range to also include the breath-peaks just before and after the set above
				const breathPeaksPossiblyInBreathCycleRange = recentPeaks_breath.slice(
					(recentPeaks_breath.indexOf(closestBreathPeaksBeforeAndAfter[0]) - 1).KeepAtLeast(0),
					(recentPeaks_breath.indexOf(closestBreathPeaksBeforeAndAfter.Last()) + 1) + 1
				);
				const distForMaxDesyncAmount = (currentSirenStart.sirenConfig.cycleTime * 1000) / 2;
				const breathPeaksInRange = breathPeaksPossiblyInBreathCycleRange.filter(a=>a.sample.time.Distance(sirenPeak.time) <= distForMaxDesyncAmount);

				const desyncAmount_forSirenPeak = breathPeaksInRange.length
					// if breath-peaks found in range, calculate result as sum of their desync-amounts (when person is conscious / syncing their breath, there's virtually never >1 breath per range; so high penalties when summed is just fine)
					? breathPeaksInRange.map(breathPeak=>GetPercentFromXToY(0, distForMaxDesyncAmount, breathPeak.sample.time.Distance(sirenPeak.time))).Sum().KeepAtMost(1) // ensure we don't go over 1 (100% desync)
					// else (if no breath-peak in range), declare max desync for this breath-cycle
					: 1;

				// if this is the last siren-peak, and our calculated desync is the max, assume the corresponding breath-peak just isn't discernable yet (algo requires significant space around peak); so skip this entry for the averaging
				if (i == recentPeaks_siren.length - 1 && desyncAmount_forSirenPeak == 1) continue; //recentPeaks_desyncAmounts.push(null);
				recentPeaks_desyncAmounts.push(desyncAmount_forSirenPeak);
			}
			const desyncAmount_forWindow = recentPeaks_desyncAmounts.concat(0).Average();

			/*if (type == "long" && forLiveSession) {
				Log_Direct(`Siren desync-peaks-details: ${desyncAmount_forWindow.ToPercentStr(0)} `,
					"@recentPeaks_siren:", recentPeaks_siren,
					`@recentPeaks_breath:`, recentPeaks_breath,
					"@recentPeaks_siren_times:", recentPeaks_siren.map(a=>moment(a.time).format("HH:mm:ss.SSS")),
					`@recentPeaks_breath_times:`, recentPeaks_breath.map(a=>moment(a.sample.time).format("HH:mm:ss.SSS")),
				);
			}*/

			return desyncAmount_forWindow;
		} else if (calcType == DesyncCalcType.slopes) {
			const sampleSlopesOverLastXSeconds_breath = this.GetSlopesOfSamplesInRange(startTime, endTime);
			const sampleSlopesOverLastXSeconds_breath_shiftedToAccountForResponseTime = sampleSlopesOverLastXSeconds_breath.map(a=>a.time - (responseTime * 1000));
			const sampleSlopesOverLastXSeconds_siren = PoliceSiren_GetSlopesOfSamplesMatchingGivenTimes(sampleSlopesOverLastXSeconds_breath_shiftedToAccountForResponseTime, currentSirenStart.time, currentSirenStart.sirenConfig.cycleTime);
			// if not enough samples collected over the given window, assume start of session or belt disconnect, so return null
			// (allow up to 0.5s of "missing samples", to account for samples whose transfer failed, or just have not yet been received -- standard BT transfer delay is ~?ms)
			const samplesExpectedInWindow = (windowSize / 1000) * Belt_samplesPerSecond;
			if (sampleSlopesOverLastXSeconds_breath.length < samplesExpectedInWindow - (0.5 * Belt_samplesPerSecond)) {
				return null; // not enough samples to calculate desync
			}

			const desyncAmount = sampleSlopesOverLastXSeconds_breath.map((breathSampleSlope, i)=>{
				return breathSampleSlope.slopeType == sampleSlopesOverLastXSeconds_siren[i].type ? 0 : 1;
			}).Average();
			return desyncAmount;
		} else {
			AssertUnreachable(calcType);
		}
	}

	/** Returns the times of each breath-cycle peak, in the last X seconds. (array is ordered chronologically, from oldest to newest)
	 * The peaks are defined (roughly) as the points where the respiration sample-values reach their peak (highest) value in each cycle. [see PeakDetection_Prominence.ts for details]
	 * See also: SirenSound.GetCyclePeaksInLastXSeconds */
	GetBreathPeaksInRange(rangeStartTime: number, rangeEndTime: number, opt = {} as {minDistBetweenPeaksMs?: number, windowSizeMs?: number}): Peak[] {
		const {minDistBetweenPeaksMs = BREATH_PEAKS_CALC_MIN_DIST_BETWEEN_PEAKS_DEFAULT, windowSizeMs = BREATH_PEAKS_CALC_WINDOW_SIZE_DEFAULT} = opt;
		const sampleSearchRange_start = rangeStartTime - windowSizeMs; // for collecting force-samples, extend range back even further (by peak-detection window-size) so peak detection works well even at start of target range

		//const forceSamplesInRange = this.samples_force.filter(a=>a.time >= sampleSearchRange_start && a.time <= rangeEndTime);
		const firstSampleInRange_index = BinarySearch(this.samples_force, sample=>sampleSearchRange_start - sample.time).GetIndexOfMatch_OrInsertPointPlusX(0);
		const lastSampleInRange_index = BinarySearch(this.samples_force, sample=>rangeEndTime - sample.time, {minI: firstSampleInRange_index}).GetIndexOfMatch_OrInsertPointPlusX(-1);
		const forceSamplesInRange = this.samples_force.slice(firstSampleInRange_index, lastSampleInRange_index + 1);

		const result = DetectBreathingPeaks(forceSamplesInRange, {
			minProminence_rel: this.Config.sync_minBreathPeakProminence, //: g.t1 ?? 0.1, // 10% of signal range
			minProminence_abs: this.Config.sync_minBreathPeakHeight,
			minDistBetweenPeaksMs, //g.t3 ?? 2000,
			windowSizeMs, //g.t2 ?? 20000, // 20 seconds
		});
		// correct the sample-indexes of the found-peaks (since the array of force-samples we sent into DetectBreathingPeaks was a subset of the full samples-force array)
		for (const peak of result) peak.sampleIndex = firstSampleInRange_index + peak.sampleIndex;
		return result;
	}

	GetSlopesOfSamplesInRange(startTime: number, endTime: number) {
		const slopes = [] as {time: number, slopeAmount: number, slopeType: "up" | "down"}[];
		for (let i = this.samples_force.length - 1; i >= 1; i--) {
			const prevSample = this.samples_force[i - 1];
			const sample = this.samples_force[i];
			// if we reach a sample that is older than the minimum time, stop processing (since all the earlier ones will be out of range as well)
			if (sample.time < startTime) break;
			// if we're at a sample that is newer than the maximum time, simply ignore it (and continue iterating backward in the array)
			if (sample.time > endTime) continue;

			const slopeAmount = sample.value - prevSample.value;
			slopes.push({time: sample.time, slopeAmount, slopeType: slopeAmount > 0 ? "up" : "down"});
		}
		return slopes.reverse();
	}

	// breath-interval calculation
	// ----------

	/** Before calling, fields [samples_force, breathPeaks] must be populated. */
	CalculateBreathIntervalSamples_Long() {
		this.CalculateBreathIntervalSamples_X(()=>this.samples_breathInterval_long, BREATH_INTERVAL_CALC_WINDOW_SIZE_LONG, "average");
	}
	/** Before calling, fields [samples_force, breathPeaks] must be populated. */
	CalculateBreathIntervalSamples_Short() {
		this.CalculateBreathIntervalSamples_X(()=>this.samples_breathInterval_short, BREATH_INTERVAL_CALC_WINDOW_SIZE_SHORT, "average");
	}
	private CalculateBreathIntervalSamples_X(intervalSamplesArrayToModify_getter: ()=>Array<number|null>, windowSizeMs: number, basis: "average" | "median") {
		const intervalSamplesArray = intervalSamplesArrayToModify_getter();
		// fill array with nulls (we use this unusual approach, because we must use mutation-only here)
		intervalSamplesArray.length = this.samples_force.length;
		//intervalSamplesArray.fill(null);
		for (let i = 0; i < intervalSamplesArray.length; i++) {
			intervalSamplesArray[i] = null; // ensure all entries are null
		}

		for (let sampleIndex = 0; sampleIndex < this.samples_force.length; sampleIndex++) {
			const currentSample = this.samples_force[sampleIndex];
			const breathInterval = this.GetBreathIntervalForWindow(currentSample.time, windowSizeMs, basis, null);
			intervalSamplesArray[sampleIndex] = breathInterval;
		}
	}
	/** Result is in seconds. */
	GetBreathIntervalForWindow(windowEndTime: number, windowSizeMs: number, basis: "average" | "median", searchBackFromPeakIndex: number|null) {
		const breathPeaksInLastMinute = this.GetBreathPeaksInRange_FromPrecalcData({rangeStartTime: windowEndTime - minuteInMS, rangeEndTime: windowEndTime, searchBackFromPeakIndex});
		if (!AreBreathPeaksInWindowValid(breathPeaksInLastMinute, windowEndTime - minuteInMS, windowEndTime)) return null;
		const breathPeaksInWindow = this.GetBreathPeaksInRange_FromPrecalcData({rangeStartTime: windowEndTime - windowSizeMs, rangeEndTime: windowEndTime, searchBackFromPeakIndex});
		if (!AreBreathPeaksInWindowValid(breathPeaksInWindow, windowEndTime - windowSizeMs, windowEndTime, 2)) return null; // add requirement "2+ in total", else can't calc breath-interval

		// calculate breath interval
		/*const intervals = [] as number[]; // in seconds
		for (let i = 1; i < breathPeaksInWindow.length; i++) {
			const intervalMS = breathPeaksInWindow[i].sample.time - breathPeaksInWindow[i-1].sample.time;
			if (intervalMS > BREATH_INTERVAL_CALC_REJECT_BREATHS_LONGER_THAN) continue;
			intervals.push(intervalMS / 1000); // convert to seconds
		}
		if (intervals.length < 1) return null;*/
		if (basis == "average") {
			const firstPeak = breathPeaksInWindow[0];
			const lastPeak = breathPeaksInWindow[breathPeaksInWindow.length - 1];
			const timeBetweenBoundaryPeaks = lastPeak.sample.time - firstPeak.sample.time;
			const breathInterval = (timeBetweenBoundaryPeaks / (breathPeaksInWindow.length - 1)) / 1000; // convert to seconds
			return breathInterval;

			/*if (intervals.Average() > 6 && new Date(windowEndTime).toLocaleTimeString("sv").startsWith("04:16") && !g.stop1) {
				console.log("Intervals:", intervals, breathPeaksInWindow);
			}
			return intervals.Average();*/
		} else if (basis == "median") {
			const intervals = [] as number[]; // in seconds
			for (let i = 1; i < breathPeaksInWindow.length; i++) {
				const intervalMS = breathPeaksInWindow[i].sample.time - breathPeaksInWindow[i-1].sample.time;
				//if (intervalMS > BREATH_INTERVAL_CALC_REJECT_BREATHS_LONGER_THAN) continue;
				intervals.push(intervalMS / 1000); // convert to seconds
			}
			//if (intervals.length < 1) return null;
			return intervals.Median();
		} else {
			AssertUnreachable(basis, "Invalid basis for breath-interval calculation.");
		}
	}

	// others
	// ----------

	/** Before calling, fields [samples_force, breathPeaks] must be populated. */
	CalculateBreathIntervalPercentileXSamples() {
		this.samples_breathIntervalPercentileX = this.samples_force.map(a=>null); // initialize with nulls

		for (let sampleIndex = 0; sampleIndex < this.samples_force.length; sampleIndex++) {
			const currentSample = this.samples_force[sampleIndex];
			const breathInterval = this.GetBreathIntervalLowRangeForWindow(currentSample.time);
			this.samples_breathIntervalPercentileX[sampleIndex] = breathInterval;
		}
	}
	/** Result is in seconds. */
	GetBreathIntervalLowRangeForWindow(windowEndTime: number) {
		const windowStartTime = windowEndTime - BREATH_INTERVAL_PERCENTILE_X_CALC_WINDOW_SIZE;
		const breathPeaksInWindow = this.GetBreathPeaksInRange_FromPrecalcData({rangeStartTime: windowStartTime, rangeEndTime: windowEndTime});
		if (!AreBreathPeaksInWindowValid(breathPeaksInWindow, windowStartTime, windowEndTime, 3)) return null; // add requirement "3+ in total", else wouldn't give meaningful results

		const intervals = GetBreathIntervals_WithFiltering(breathPeaksInWindow, 999, 999);
		if (intervals.length < 5) return null;

		// calculate percentile X of interval values
		const intervals_sorted = [...intervals.values()].sort((a, b)=>a - b);
		const percentileX_index = Math.floor(intervals_sorted.length * BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_X);
		const percentileY_index = Math.floor(intervals_sorted.length * BREATH_INTERVAL_PERCENTILE_X_THRESHOLD_Y);
		const intervalsInPercentileRange = intervals_sorted.slice(percentileX_index, percentileY_index + 1); // X-Y% range
		//const intervalsCappedToPercentileRange = intervals_sorted.map(val=>val.KeepBetween(intervals_sorted[percentileX_index], intervals_sorted[percentileY_index]));
		const avgOfIntervalsInPercentileRange = intervalsInPercentileRange.Average();

		return avgOfIntervalsInPercentileRange;
	}

	/** Before calling, fields [samples_force, breathPeaks] must be populated. */
	CalculateBreathIntervalVariabilitySamples() {
		this.samples_breathIntervalVariability = this.samples_force.map(a=>null); // initialize with nulls

		for (let sampleIndex = 0; sampleIndex < this.samples_force.length; sampleIndex++) {
			const currentSample = this.samples_force[sampleIndex];
			const windowStartTime = currentSample.time - BREATH_VALUE_STD_DEV_CALC_WINDOW_SIZE;
			const breathPeaksInWindow = this.GetBreathPeaksInRange_FromPrecalcData({rangeStartTime: windowStartTime, rangeEndTime: currentSample.time});
			if (!AreBreathPeaksInWindowValid(breathPeaksInWindow, windowStartTime, currentSample.time, 3)) continue; // add requirement "3+ in total", else wouldn't give meaningful results

			const intervals = GetBreathIntervals_WithFiltering(breathPeaksInWindow, 10, 5); // basically: "cap to 5s [limit of not-consciously-controlled breath-intervals], then get standard-deviation"
			//const intervals = GetBreathIntervals_WithFiltering(breathPeaksInWindow, 10, 6); // basically: "cap to 6s [target breath-interval], then get standard-deviation"
			//const intervals = GetBreathIntervals_WithFiltering(breathPeaksInWindow, 999, 999); // basically: "the raw standard-deviation, of all breath-intervals in window"
			if (intervals.length < 5) continue;

			// Calculate standard deviation of interval values
			const mean = intervals.Average();
			const squaredDifferences = intervals.map(val=>(val - mean) * (val - mean));
			const variance = squaredDifferences.Average();
			const stdDev = Math.sqrt(variance);

			this.samples_breathIntervalVariability[sampleIndex] = stdDev;
		}
	}

	/** Before calling, fields [samples_force, breathPeaks] must be populated. */
	CalculateBreathEdgeShiftSamples() {
		this.samples_breathEdgeShift = this.samples_force.map(a=>null); // initialize with nulls

		const breathPeaks_verticalShifts = this.breathPeaks.ToMap(peak=>peak, (peak, index)=>{
			const prevPeakCount = 3;
			const prevPeaks = this.breathPeaks.slice(index - prevPeakCount, index);
			if (prevPeaks.length < prevPeakCount) return 0; // not enough previous peaks to calculate difference

			const peak_trough = peak.sample.value - peak.prominences.abs_left;
			const prevPeakDiffs = prevPeaks.map(prevPeak=>{
				const peakDiff = peak.sample.value - prevPeak.sample.value;
				const prevPeak_trough = prevPeak.sample.value - prevPeak.prominences.abs_left;
				const troughDiff = peak_trough - prevPeak_trough;
				//return Math.abs(peakDiff) + Math.abs(troughDiff);
				return Math.min(Math.abs(peakDiff), Math.abs(troughDiff));
				/*const moveDirIsSame = (peakDiff >= 0) == (prevPeak_trough >= 0);
				if (!moveDirIsSame) return 0;
				return Math.min(Math.abs(peakDiff), Math.abs(troughDiff)) // if both peaks are on the same side of the trough, return the smaller difference*/
			});
			// of the last X previous-peaks, return the combined "diff" that is the lowest (to avoid single outliers having too much influence; we're interested in larger-scale shifts)
			return prevPeakDiffs.Min();
		});

		// cap extreme values using winsorization (90th percentile), so that extreme outliers (eg. during repositioning) don't skew the results too much
		const breathPeaks_verticalShifts_sortedValues = [...breathPeaks_verticalShifts.values()].sort((a, b)=>a - b);
		const percentile90 = breathPeaks_verticalShifts_sortedValues[Math.floor(breathPeaks_verticalShifts_sortedValues.length * .9)];
		const breathPeaks_verticalShifts_winsorized = [...breathPeaks_verticalShifts.entries()].ToMap(a=>a[0], a=>Math.min(a[1], percentile90));

		for (let sampleIndex = 0; sampleIndex < this.samples_force.length; sampleIndex++) {
			const currentSample = this.samples_force[sampleIndex];
			const windowStartTime = currentSample.time - BREATH_EDGE_SHIFT_CALC_WINDOW_SIZE;

			// collect breath peaks within the window (using binary search for efficiency)
			const breathPeaksInWindow = this.GetBreathPeaksInRange_FromPrecalcData({rangeStartTime: windowStartTime, rangeEndTime: currentSample.time}); //, searchBackFromPeakIndex: this.breathPeaks.length - 1});
			if (!AreBreathPeaksInWindowValid(breathPeaksInWindow, windowStartTime, currentSample.time, 3)) continue; // add requirement "3+ in total", else wouldn't give meaningful results

			const breathPeaksInWindow_verticalShift = breathPeaksInWindow.map(peak=>{
				//const peakDiffOrTroughDiff = breathPeaks_verticalShifts.get(peak);
				const peakDiffOrTroughDiff = breathPeaks_verticalShifts_winsorized.get(peak);
				// if no value found, assume 0 (this can happen if the peak is the first one in the list, or if the peak is not in the map)
				if (peakDiffOrTroughDiff == null) return 0;
				return peakDiffOrTroughDiff; // / Math.abs(peak.sample.value); // relative height variability
			}).Average();
			this.samples_breathEdgeShift[sampleIndex] = breathPeaksInWindow_verticalShift;
		}
	}

	// DEILD detection
	// ==========

	/**
	 * Updates the longInhales array based on recent breath data.
	 * Similar to UpdateBreathPeaks, this only processes new samples since the last update.
	 * @param sessionEvents Session events to check for phase changes (only detect DEILD attempts during dreaming phase)
	 * @param considerPeaksInLastXSeconds Only consider peaks within the last X seconds (null to consider all peaks)
	 * @note The maxJitter config value is now interpreted as the maximum allowed smoothness (coefficient of variation) for the inhale signal
	 */
	UpdateLongInhales(sessionEvents: SessionEvent[]|null, considerPeaksInLastXSeconds: number|n) {
		if (this.samples_force.length < 2) return;
		if (this.breathPeaks.length < 2) return; // need at least 2 peaks to detect troughs
		if (
			this.Config.maxSampleGapInSequence_base == null || this.Config.longInhaleDetectionConfigs == null || this.Config.longInhaleDetectionConfigs.length == 0
		) return;

		const now = this.GetNowTime();
		const sessionEvents_onlyPhaseSets = sessionEvents?.filter(event=>event.type == "Alarms.PhaseSet") ?? [];

		// reset the debug-data instrumentation buffer (it gets re-populated inline, below, for the matching trough)
		const debug_startTime = this.options?.longInhaleDebug_startTime;
		const debug_endTime = this.options?.longInhaleDebug_endTime;
		const debugRangeActive = debug_startTime != null && debug_endTime != null;
		if (debugRangeActive) this.longInhaleDebugData = new Map();
		// find the global sample-index of the debug-window's start sample (first sample at-or-after the debug-range's start)
		const debug_startSampleIndex = debugRangeActive
			? BinarySearch(this.samples_force, s=>debug_startTime - s.time).GetIndexOfMatch_OrInsertPointPlusX(0)
			: -1;

		// For each breath peak in the recent region, check if it could be the end of a long-inhale
		const lastOldLongInhale = this.longInhales.LastOrX();

		// Get breath peaks that are within the last X seconds AND which are after all already-discovered long-inhales
		const relevantPeaks = this.breathPeaks.filter(peak=>{
			// Only consider peaks in the last X seconds (long enough we're sure to catch the last peak/exhale-start-point of any valid DEILD-attempt-start/having-just-woken context)
			if (considerPeaksInLastXSeconds != null && peak.sample.time < now - (considerPeaksInLastXSeconds * secondInMS)) return false;
			// Only consider peaks that are after all already-discovered long-inhales
			if (lastOldLongInhale && peak.sample.time < lastOldLongInhale.timeOfSignalCompleted) return false;
			return true;
		});

		//let phaseAsOfCurrentPeak = Phase.notStarted;
		for (const peak of relevantPeaks) {
			// Get the next peak after this one (if it exists)
			const nextPeakIndex = this.breathPeaks.indexOf(peak) + 1;
			const nextPeak = nextPeakIndex < this.breathPeaks.length ? this.breathPeaks[nextPeakIndex] : null;

			// Get samples after current-peak (either until next-peak [inclusive], or all remaining samples)
			const spanStartIndex = BinarySearch(this.samples_force, s=>peak.sample.time - s.time).GetIndexOfMatch_OrInsertPointPlusX(0);
			const spanEndIndex = nextPeak
				? BinarySearch(this.samples_force, s=>nextPeak.sample.time - s.time, {minI: spanStartIndex}).GetIndexOfMatch_OrInsertPointPlusX(0) + 1
				: this.samples_force.length;
			/** All samples starting from current-peak (inclusive) and ending with next-peak (inclusive). */
			const spanSamples_betweenPeaks = this.samples_force.slice(spanStartIndex, spanEndIndex);
			if (spanSamples_betweenPeaks.length < 2) continue;

			// Find the trough (lowest value) after current-peak
			let troughIndex = 0;
			let troughValue = spanSamples_betweenPeaks[0].value;
			for (let i = 1; i < spanSamples_betweenPeaks.length; i++) {
				if (spanSamples_betweenPeaks[i].value < troughValue) {
					troughValue = spanSamples_betweenPeaks[i].value;
					troughIndex = i;
				}
			}

			// Use a sliding window approach to check for valid DEILD attempts
			// Start from the trough and slide forward until we pass the next peak
			//let detectedSignal_windowInfo: {firstSample: BreathSample, lastSample: BreathSample}|n;
			//let detectedSignalBetweenThesePeaks = false;

			const detectionConfigs = this.Config.longInhaleDetectionConfigs;
			// precompute the global minimum min-duration across all configs (used for early-exit optimization)
			const inhale_minDuration_globalMin = detectionConfigs.reduce((min, dc)=>Math.min(min, dc.minDuration, dc.minDuration_phaseDreaming), Infinity);
			for (let windowStartIndex = troughIndex; windowStartIndex < spanSamples_betweenPeaks.length; windowStartIndex++) {
				const result = TryFindLongInhaleForSpecificWindowStart({
					spanSamples: spanSamples_betweenPeaks,
					spanSamples_globalIndexOfFirst: spanStartIndex,
					windowStartIndexInSpan: windowStartIndex,
					recordInstrumentation: false,
					nextPeak, now,
					maxSampleGapInSequence_base: this.Config.maxSampleGapInSequence_base,
					inhale_minDuration_globalMin, detectionConfigs, sessionEvents_onlyPhaseSets,
				});
				if (result.detectedInhale) {
					this.longInhales.push(result.detectedInhale);
					//detectedSignalBetweenThesePeaks = true;
					break;
				}
			}

			// if the debug window-start falls within the current peak->nextPeak span, run one extra instrumentation-recording call for that specific window-start
			// [note: we exclude the next-peak sample itself, to ensure no double span-instrumentation]
			const debugWindowStartsInThisSpan = debugRangeActive && debug_startSampleIndex >= spanStartIndex && debug_startSampleIndex < spanEndIndex - 1;
			if (debugWindowStartsInThisSpan) {
				const debugResult = TryFindLongInhaleForSpecificWindowStart({
					spanSamples: spanSamples_betweenPeaks,
					spanSamples_globalIndexOfFirst: spanStartIndex,
					windowStartIndexInSpan: debug_startSampleIndex - spanStartIndex,
					nextPeak, now,
					maxSampleGapInSequence_base: this.Config.maxSampleGapInSequence_base,
					inhale_minDuration_globalMin, detectionConfigs, sessionEvents_onlyPhaseSets,
					recordInstrumentation: true,
				});
				for (const [k, v] of debugResult.debugDataEntries) this.longInhaleDebugData.set(k, v);
			}
		}
	}
}

/**
 * Calculates the roughness of a breath signal using coefficient of variation of derivatives.
 * @param signal Array of breath sample values
 * @returns Coefficient of variation (high = rough, low = smooth). Returns 0 if unable to calculate.
 */
function CalculateBreathLineRoughness(signal: number[]): number {
	if (signal.length < 2) return 0;

	// Calculate first derivative (rate of change)
	const derivatives: number[] = [];
	for (let i = 1; i < signal.length; i++) {
		derivatives.push(signal[i] - signal[i - 1]);
	}

	if (derivatives.length === 0) return 0;

	// Calculate variance of derivatives
	const mean = derivatives.reduce((a, b)=>a + b, 0) / derivatives.length;
	const variance = derivatives.reduce((sum, val)=>sum + Math.pow(val - mean, 2), 0) / derivatives.length;

	// Return coefficient of variation (normalized)
	const stdDev = Math.sqrt(variance);
	// Avoid division by zero
	if (Math.abs(mean) < 0.0001) return stdDev; // If mean is near zero, just return stdDev
	const cv = stdDev / Math.abs(mean);

	return cv; // Lower = smoother
}

/** Calculates the long-inhale detection result for a single window-start candidate (within a sample-span, ie. between two peaks).
 * Note: This function is pure/side-effect-free. */
function TryFindLongInhaleForSpecificWindowStart(p: {
	spanSamples: BreathSample[],
	spanSamples_globalIndexOfFirst: number,
	windowStartIndexInSpan: number,
	nextPeak: Peak|null,
	now: number,
	maxSampleGapInSequence_base: number,
	inhale_minDuration_globalMin: number,
	detectionConfigs: NonNullable<EngineConfig_Breath_ForBreathProcessor["longInhaleDetectionConfigs"]>,
	sessionEvents_onlyPhaseSets: SessionEvent[],
	recordInstrumentation: boolean,
}): {detectedInhale: LongInhale|null, debugDataEntries: [number, LongInhaleDebugSample][]} {
	const {
		spanSamples, spanSamples_globalIndexOfFirst, windowStartIndexInSpan, nextPeak, now,
		maxSampleGapInSequence_base, inhale_minDuration_globalMin, detectionConfigs,
		sessionEvents_onlyPhaseSets, recordInstrumentation,
	} = p;
	const result = {
		detectedInhale: null as LongInhale|null,
		debugDataEntries: [] as [number, LongInhaleDebugSample][],
	};

	const windowStartSample = spanSamples[windowStartIndexInSpan];

	// optimization: if there aren't enough samples before next peak to possibly meet any min-duration requirement, stop sliding
	const timeAvailableBeforeNextPeak = (nextPeak ? nextPeak.sample.time : now) - windowStartSample.time;
	if (!recordInstrumentation && timeAvailableBeforeNextPeak < inhale_minDuration_globalMin * 1000) {
		return result;
	}

	// determine "current phase" based on window-start position (simpler logic, a bit faster, and neither is strictly "correct" anyway)
	const currentPhase = FindPhaseBasedOnSessionEventsBeforeTimeX(sessionEvents_onlyPhaseSets, spanSamples[0].time);

	// build a window of samples starting from this index
	const inhaleSamples = [] as BreathSample[];

	// collect samples until we meet the requirements of at least one detection config
	for (let i = windowStartIndexInSpan; i < spanSamples.length; i++) {
		const prevSample = spanSamples[i - 1] as BreathSample|n;
		const thisSample = spanSamples[i];

		// check for sample gaps (if gap exists, this window/window-start-candidate is immediately discarded, as it's impossible for this to be "fixed" by looking at more samples)
		if (prevSample) {
			const sampleGap = thisSample.time - prevSample.time;
			if (sampleGap > maxSampleGapInSequence_base * 1000) {
				break;
			}
		}

		inhaleSamples.push(thisSample);

		const durationSoFar = thisSample.time - windowStartSample.time;
		const ascentSoFar = thisSample.value - windowStartSample.value;
		let roughness: number|n; // lazily computed, since CalculateBreathLineRoughness is relatively expensive
		const calcRoughnessIfNotYet = ()=>roughness ??= CalculateBreathLineRoughness(inhaleSamples.map(s=>s.value));

		// if we're recording instrumentation data for this window-start -> forward
		if (recordInstrumentation) {
			calcRoughnessIfNotYet(); // calc roughness immediately
			// record the details of the metrics for this [debug window start -> to current sample] subwindow
			result.debugDataEntries.push([spanSamples_globalIndexOfFirst + i, {durationSoFar, ascentSoFar, roughness}]);
		}

		// only do the phase lookup + per-config checks once the global minimum duration is reached
		if (durationSoFar < inhale_minDuration_globalMin * 1000) continue;

		// check each config to see if this window meets the requirements
		for (const dc of detectionConfigs) {
			if (ascentSoFar < dc.minAscendHeight) continue;
			const minDuration_forCurrentPhase = currentPhase == Phase.dreaming ? dc.minDuration_phaseDreaming : dc.minDuration;
			if (durationSoFar < minDuration_forCurrentPhase * 1000) continue;
			calcRoughnessIfNotYet(); // lazily compute roughness (shared across all configs for this sample)
			if (roughness! > dc.maxRoughness) continue;

			// all conditions met - this is a valid long-inhale detection! (only store the first discovered)
			result.detectedInhale ??= {timeOfSignalStart: windowStartSample.time, timeOfSignalCompleted: thisSample.time};
			// if not recording instrumentation, we can return the detection right now
			if (!recordInstrumentation) return result;
			// else, pretend we haven't found a result, and keep looping (since we want complete instrumentation data)
		}
	}

	return result;
}

/** Note: This function only looks at "Alarms.PhaseSet" events, so you can optimize calls to this func, by pre-filtering the passed events-array to events of that type. */
export function FindPhaseBasedOnSessionEventsBeforeTimeX(sessionEvents: SessionEvent[]|null, timeX: number): Phase|n {
	if (!sessionEvents) return null;
	const dividerPoint = timeX - .001;
	// this *should* always be insert-point minus 1, since we make divider-point invalid timestamp
	const firstEventBeforeHere = BinarySearch(sessionEvents, event=>dividerPoint - event.date).GetIndexOfMatch_OrInsertPointPlusX(-1);
	let phaseAtHere: Phase|n;
	for (let i = firstEventBeforeHere; i >= 0; i--) {
		const ev = sessionEvents[i];
		if (ev.type == "Alarms.PhaseSet") {
			phaseAtHere = ev.phase;
			break;
		}
	}
	return phaseAtHere;
}

export function AreBreathPeaksInWindowValid(breathPeaksInWindow: Peak[], rangeStartTime: number, rangeEndTime: number, minPeaksOverall = 0) {
	// if range is at least 1m, but we find <6 breath-peaks in the last 1m, assume respiration-belt is off, so mark breath-peaks data as invalid
	if (rangeEndTime - rangeStartTime >= 60000 && breathPeaksInWindow.filter(a=>a.sample.time >= rangeEndTime - minuteInMS).length < 6) return false;
	// if caller provided a minimum number of peaks, check that as well
	if (breathPeaksInWindow.length < minPeaksOverall) return false;
	return true;
}

/** Returned breath-intervals are in seconds. */
export function GetBreathIntervals_WithFiltering(breathPeaks: Peak[], ignoreIntervalsLongerThan: number, capIntervalsLongerThan: number) {
	const intervals = [] as number[]; // in seconds
	for (let i = 1; i < breathPeaks.length; i++) {
		const rawInterval = breathPeaks[i].sample.time - breathPeaks[i-1].sample.time;
		const intervalInSeconds = rawInterval / 1000;

		// if interval longer than X, reject (must be a repositioning or the like)
		if (intervalInSeconds > ignoreIntervalsLongerThan) continue;
		// if interval longer than Y [generally, the target breath-interval], cap to Y (to avoid possibly-incorrect outliers skewing the results too much)
		if (intervalInSeconds > capIntervalsLongerThan) {
			intervals.push(capIntervalsLongerThan);
			continue;
		}

		intervals.push(intervalInSeconds);
	}
	return intervals;
}